import React, { useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';
import timesheetCacheService from '../../../services/timesheetCacheService';

// Constants for validation
const MIN_HOURS = 0.01;
const MAX_HOURS = 24;
const MAX_MINUTES = 59;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_URL_LENGTH = 2000;

const TimeTrackerForm = ({ contracts, onEntryCreated }) => {
  const { userProfile } = useAuth();
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [hoursWorked, setHoursWorked] = useState('');
  const [minutesWorked, setMinutesWorked] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);

  // Input validation helpers
  const validateDescription = (text) => {
    if (!text || text.trim().length === 0) return false;
    if (text.length > MAX_DESCRIPTION_LENGTH) return false;
    return true;
  };

  const validateUrl = (url) => {
    if (!url) return true; // URL is optional
    if (url.length > MAX_URL_LENGTH) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const sanitizeDescription = (text) => {
    return text.trim().substring(0, MAX_DESCRIPTION_LENGTH);
  };

  // Calculate total hours from inputs
  const calculateTotalHours = () => {
    const hours = parseFloat(hoursWorked || 0);
    const minutes = parseFloat(minutesWorked || 0);
    return hours + (minutes / 60);
  };

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!selectedContractId) {
      setError('Please select a contract');
      return;
    }

    if (!validateDescription(description)) {
      setError('Please enter a task description (max 500 characters)');
      return;
    }

    const hours = parseFloat(hoursWorked || 0);
    const minutes = parseFloat(minutesWorked || 0);

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes > MAX_MINUTES) {
      setError('Please enter valid hours (0-24) and minutes (0-59)');
      return;
    }

    const totalHours = calculateTotalHours();
    
    if (totalHours < MIN_HOURS) {
      setError(`Minimum time entry is ${MIN_HOURS} hours`);
      return;
    }

    if (totalHours > MAX_HOURS) {
      setError(`Maximum time entry is ${MAX_HOURS} hours`);
      return;
    }

    if (!validateUrl(documentUrl)) {
      setUrlError('Invalid URL format');
      return;
    }

    setIsCreating(true);
    setError(null);
    setUrlError(null);

    try {
      const contract = contracts.find(c => c.id === selectedContractId);

      if (!contract) {
        setError('Selected contract not found');
        setIsCreating(false);
        return;
      }

      if (!userProfile?.id) {
        setError('User profile not loaded');
        setIsCreating(false);
        return;
      }

      const now = new Date();
      const startTime = new Date(now.getTime() - (totalHours * 60 * 60 * 1000));
      const hoursWorkedValue = parseFloat(totalHours.toFixed(2));
      const hourlyRate = contract.hourly_rate || 0;

      // Build description with document URL if provided
      let finalDescription = sanitizeDescription(description);
      if (documentUrl) {
        finalDescription = `${finalDescription}\n\nDocument: ${documentUrl}`;
      }

      const { error: insertError } = await supabase
        .from('timesheet_entries')
        .insert({
          contract_id: selectedContractId,
          project_id: selectedProjectId || null,
          va_id: userProfile.id,
          employer_id: contract.employer_id,
          start_time: startTime.toISOString(),
          end_time: now.toISOString(),
          date: startTime.toISOString().split('T')[0],
          hours_logged: hoursWorkedValue,
          hours_worked: hoursWorkedValue,
          hourly_rate: hourlyRate,
          total_amount: hoursWorkedValue * hourlyRate,
          description: finalDescription,
          is_billable: true,
          status: 'pending'
        });

      if (insertError) throw insertError;

      // Invalidate cache after successful creation
      console.log('[TimeTrackerForm] Invalidating timesheet cache after new entry');
      await timesheetCacheService.invalidateAllTimesheetData(userProfile.id);

      // Reset form
      setDescription('');
      setDocumentUrl('');
      setHoursWorked('');
      setMinutesWorked('');
      setSelectedContractId('');
      setSelectedProjectId('');
      setError(null);
      setUrlError(null);
      
      // Refresh entries
      if (onEntryCreated) onEntryCreated();
      
    } catch (err) {
      console.error('Error creating time entry:', err);
      setError(`Failed to create time entry: ${err.message || 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  }, [selectedContractId, selectedProjectId, description, documentUrl, hoursWorked, minutesWorked, contracts, userProfile?.id, onEntryCreated]);

  // Get selected contract details
  const selectedContract = contracts.find(c => c.id === selectedContractId);
  const totalHours = calculateTotalHours();
  const selectedContractData = contracts.find(c => c.id === selectedContractId);
  const totalAmount = totalHours * (selectedContractData?.hourly_rate || 0);

  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        <Icon name="Clock" size={20} className="inline mr-2" />
        Time Entry Form
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Contract Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contract / Employer *
          </label>
          <select
            value={selectedContractId}
            onChange={(e) => {
              setSelectedContractId(e.target.value);
              setSelectedProjectId('');
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select a contract</option>
            {contracts.map(contract => (
              <option key={contract.id} value={contract.id}>
                {contract.jobs?.title || 'Contract'} - {contract.employer?.company_name || contract.employer?.full_name} (${contract.hourly_rate}/hr)
              </option>
            ))}
          </select>
        </div>

        {/* Project Selection (conditional) */}
        {selectedContract && selectedContract.projects && selectedContract.projects.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project (Optional)
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">General work (no specific project)</option>
              {selectedContract.projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Task Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Task Description *
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What did you work on? Be specific to help your employer understand your work."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
          <div className="text-xs text-gray-500 mt-1">
            {description.length}/{MAX_DESCRIPTION_LENGTH} characters
          </div>
        </div>

        {/* Document URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document URL / Website URL (Optional)
          </label>
          <input
            type="url"
            value={documentUrl}
            onChange={(e) => {
              setDocumentUrl(e.target.value);
              if (e.target.value && !validateUrl(e.target.value)) {
                setUrlError('Invalid URL format');
              } else {
                setUrlError(null);
              }
            }}
            placeholder="https://example.com/document"
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
              urlError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'
            }`}
          />
          {urlError && (
            <div className="text-xs text-red-600 mt-1">{urlError}</div>
          )}
          <div className="text-xs text-gray-500 mt-1">
            Link to work samples, screenshots, or documentation
          </div>
        </div>

        {/* Time Input Section */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <div className="text-sm font-medium text-gray-900">Time Worked *</div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hours (0-24)
              </label>
              <input
                type="number"
                min="0"
                max="24"
                step="1"
                value={hoursWorked}
                onChange={(e) => setHoursWorked(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minutes (0-59)
              </label>
              <input
                type="number"
                min="0"
                max="59"
                step="1"
                value={minutesWorked}
                onChange={(e) => setMinutesWorked(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Real-time Calculation Display */}
          {(hoursWorked || minutesWorked) && (
            <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-sm font-medium text-blue-900 mb-1">
                Total Time: {totalHours.toFixed(2)} hours
              </div>
              {selectedContractData?.hourly_rate && (
                <div className="text-sm text-blue-700">
                  Billable Amount: ${totalAmount.toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          iconName="Check"
          disabled={isCreating || !selectedContractId || !description || (!hoursWorked && !minutesWorked)}
          className="w-full"
        >
          {isCreating ? 'Submitting...' : 'Submit Time Entry'}
        </Button>

        {/* Help Tips */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-600">
              <p className="font-medium mb-1">Tips for manual time entry:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Enter the hours and minutes you worked</li>
                <li>Attach documents or links to your work samples</li>
                <li>Be specific in your description for easy approval</li>
                <li>All entries are submitted as "pending" and require employer approval</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TimeTrackerForm;
